import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { supabase } from './lib/supabase';
import useStore from './store/useStore';
import { useDataLoader } from './hooks/useSupabase';
import { useAutoSnapshot } from './hooks/useAutoSnapshot';
import { useAutoPostings } from './hooks/useAutoPostings';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import Paywall from './pages/Paywall';
import Dashboard from './pages/Dashboard';
import MasterAccounts from './pages/finance/MasterAccounts';
import BalanceSheetMonthly from './pages/finance/BalanceSheetMonthly';
import CashFlow from './pages/finance/CashFlow';
import Investments from './pages/finance/Investments';
import Subscriptions from './pages/finance/Subscriptions';
import Taxes from './pages/finance/Taxes';
import PaycheckSnapshot from './pages/finance/PaycheckSnapshot';
import MECloseInstructions from './pages/finance/MECloseInstructions';
// Hidden sections — coming soon
// import HabitTracker from './pages/productivity/HabitTracker';
// import DailyPlanner from './pages/productivity/DailyPlanner';
// import Tasks from './pages/productivity/Tasks';
// import BucketList from './pages/life/BucketList';
// import ReadingList from './pages/life/ReadingList';
// import Places from './pages/life/Places';
// import Network from './pages/life/Network';
// import FitnessDashboard from './pages/health/FitnessDashboard';
// import BodyMetrics from './pages/health/BodyMetrics';
// import Nutrition from './pages/health/Nutrition';
// import Workouts from './pages/health/Workouts';
import Settings from './pages/Settings';
import { Compass } from 'lucide-react';

function Placeholder({ title }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-text-primary mb-2">{title}</h2>
        <p className="text-text-secondary">Coming soon</p>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Compass className="w-8 h-8 text-accent animate-spin" />
        <p className="text-sm text-text-secondary">Loading Meridian...</p>
      </div>
    </div>
  );
}

function ErrorFallback() {
  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-text-primary mb-2">Something went wrong</h2>
        <p className="text-text-secondary mb-4">An unexpected error occurred.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-accent text-white rounded-lg hover:opacity-90"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  useDataLoader();
  useAutoSnapshot();
  useAutoPostings();
  const loading = useStore((s) => s.loading);

  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="finance/master-accounts" element={<MasterAccounts />} />
        <Route path="finance/cashflow" element={<CashFlow />} />
        <Route path="finance/balance-sheet-monthly" element={<BalanceSheetMonthly />} />
        <Route path="finance/paycheck" element={<PaycheckSnapshot />} />
        <Route path="finance/investments" element={<Investments />} />
        <Route path="finance/subscriptions" element={<Subscriptions />} />
        <Route path="finance/taxes" element={<Taxes />} />
        <Route path="finance/me-close" element={<MECloseInstructions />} />
        {/* Hidden sections — coming soon */}
        {/* <Route path="health/dashboard" element={<FitnessDashboard />} /> */}
        {/* <Route path="health/nutrition" element={<Nutrition />} /> */}
        {/* <Route path="health/body-metrics" element={<BodyMetrics />} /> */}
        {/* <Route path="health/workouts" element={<Workouts />} /> */}
        {/* <Route path="productivity/habits" element={<HabitTracker />} /> */}
        {/* <Route path="productivity/planner" element={<DailyPlanner />} /> */}
        {/* <Route path="productivity/tasks" element={<Tasks />} /> */}
        {/* <Route path="life/bucket-list" element={<BucketList />} /> */}
        {/* <Route path="life/reading" element={<ReadingList />} /> */}
        {/* <Route path="life/places" element={<Places />} /> */}
        {/* <Route path="network" element={<Network />} /> */}
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  const session = useStore((s) => s.session);
  const setSession = useStore((s) => s.setSession);
  const [subscriptionActive, setSubscriptionActive] = useState(null); // null = loading, true/false = resolved

  useEffect(() => {
    if (!supabase) return;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        // Reset subscription check on auth change
        if (!session) setSubscriptionActive(null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Check subscription status after login
  useEffect(() => {
    if (!supabase || !session) return;

    const checkSubscription = async () => {
      const { data } = await supabase
        .from('user_settings')
        .select('subscription_active')
        .single();

      setSubscriptionActive(data?.subscription_active === true);
    };

    // Handle Stripe checkout success redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      // Clear URL param, then poll briefly for webhook to update DB
      window.history.replaceState({}, '', window.location.pathname);
      const poll = setInterval(async () => {
        const { data } = await supabase
          .from('user_settings')
          .select('subscription_active')
          .single();
        if (data?.subscription_active) {
          setSubscriptionActive(true);
          clearInterval(poll);
        }
      }, 1500);
      // Stop polling after 15s and check once more
      setTimeout(() => {
        clearInterval(poll);
        checkSubscription();
      }, 15000);
    } else {
      checkSubscription();
    }
  }, [session]);

  if (!supabase) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <p className="text-text-secondary text-sm">Missing Supabase credentials. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local</p>
      </div>
    );
  }

  if (!session) return <Login />;

  // Show loading while checking subscription
  if (subscriptionActive === null) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Compass className="w-5 h-5 text-accent animate-spin" />
          <span className="text-text-secondary text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  // Show paywall if not subscribed
  if (!subscriptionActive) return <Paywall />;

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <AuthenticatedApp />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
